import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Modal, Form, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { db } from '../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, where, limit } from 'firebase/firestore';
import AdminNavbar from '../components/AdminNavbar';
import { UI_TIMEOUTS } from '../constants/ui';

const Assessments = () => {
  const { currentUser, userProfile } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAssessment, setEditingAssessment] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    moduleId: '',
    type: 'quiz',
    duration: 60,
    passingScore: 70,
    maxAttempts: 3,
    status: 'draft',
    questions: []
  });
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });

  useEffect(() => {
    if (currentUser && userProfile?.role === 'admin') {
      loadData();
    }
  }, [currentUser, userProfile]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load assessments
      const assessmentsCollection = collection(db, 'assessments');
      const assessmentsSnapshot = await getDocs(query(assessmentsCollection, limit(200)));
      const assessmentsData = assessmentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt || Date.now())
      }));

      // Load modules
      const modulesCollection = collection(db, 'modules');
      const modulesSnapshot = await getDocs(query(modulesCollection, limit(200)));
      const modulesData = modulesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setAssessments(assessmentsData);
      setModules(modulesData);
      
    } catch (error) {
      devError('Error loading data:', error);
      showAlert('Error loading assessment data', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: '', type: '' }), 5000);
  };

  const handleShowModal = (assessment = null) => {
    if (assessment) {
      setEditingAssessment(assessment);
      setFormData({
        title: assessment.title,
        description: assessment.description || '',
        moduleId: assessment.moduleId || '',
        type: assessment.type || 'quiz',
        duration: assessment.duration || 60,
        passingScore: assessment.passingScore || 70,
        maxAttempts: assessment.maxAttempts || 3,
        status: assessment.status || 'draft',
        questions: assessment.questions || []
      });
    } else {
      setEditingAssessment(null);
      setFormData({
        title: '',
        description: '',
        moduleId: '',
        type: 'quiz',
        duration: 60,
        passingScore: 70,
        maxAttempts: 3,
        status: 'draft',
        questions: []
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingAssessment(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const assessmentData = {
        ...formData,
        duration: parseInt(formData.duration),
        passingScore: parseInt(formData.passingScore),
        maxAttempts: parseInt(formData.maxAttempts),
        updatedAt: serverTimestamp()
      };

      if (editingAssessment) {
        // Update existing assessment
        await updateDoc(doc(db, 'assessments', editingAssessment.id), assessmentData);
        showAlert('Assessment updated successfully!', 'success');
      } else {
        // Create new assessment
        await addDoc(collection(db, 'assessments'), {
          ...assessmentData,
          createdAt: serverTimestamp(),
          createdBy: currentUser.uid
        });
        showAlert('Assessment created successfully!', 'success');
      }
      
      handleCloseModal();
      loadData();
      
    } catch (error) {
      devError('Error saving assessment:', error);
      showAlert('Error saving assessment', 'danger');
    }
  };

  const handleDelete = async (assessmentId) => {
    if (window.confirm('Are you sure you want to delete this assessment? This action cannot be undone.')) {
      try {
        await deleteDoc(doc(db, 'assessments', assessmentId));
        showAlert('Assessment deleted successfully!', 'success');
        loadData();
      } catch (error) {
        devError('Error deleting assessment:', error);
        showAlert('Error deleting assessment', 'danger');
      }
    }
  };

  const getModuleName = (moduleId) => {
    const module = modules.find(m => m.id === moduleId);
    return module ? module.title : 'No Module';
  };

  const getStatusBadge = (status) => {
    const variants = {
      'draft': 'secondary',
      'active': 'success',
      'inactive': 'warning',
      'archived': 'dark'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const getTypeBadge = (type) => {
    const variants = {
      'quiz': 'primary',
      'assignment': 'info',
      'exam': 'warning',
      'project': 'success'
    };
    return <Badge bg={variants[type] || 'primary'}>{type}</Badge>;
  };

  if (userProfile?.role !== 'admin') {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <h4>Access Denied</h4>
          <p>You do not have permission to access assessment management.</p>
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <>
        <AdminNavbar currentPage="assessments" />
        <Container className="mt-4">
          <div className="text-center my-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-2">Loading assessments...</p>
          </div>
        </Container>
      </>
    );
  }

  return (
    <>
      <AdminNavbar currentPage="assessments" />
      
      <Container className="mt-4">
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1>Assessment Management</h1>
                <p className="text-muted">Create and manage quizzes, exams, and assignments</p>
              </div>
              <Button variant="primary" onClick={() => handleShowModal()}>
                Create New Assessment
              </Button>
            </div>
          </Col>
        </Row>

        {alert.show && (
          <Alert variant={alert.type} className="mb-4">
            {alert.message}
          </Alert>
        )}

        {/* Assessment Statistics */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-primary">{assessments.length}</h3>
                <small className="text-muted">Total Assessments</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-success">{assessments.filter(a => a.status === 'active').length}</h3>
                <small className="text-muted">Active Assessments</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-warning">{assessments.filter(a => a.status === 'draft').length}</h3>
                <small className="text-muted">Draft Assessments</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-info">
                  {Math.round(assessments.reduce((sum, a) => sum + (a.passingScore || 70), 0) / assessments.length) || 70}%
                </h3>
                <small className="text-muted">Avg Passing Score</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Assessments Table */}
        <Card>
          <Card.Header>
            <h5>All Assessments</h5>
          </Card.Header>
          <Card.Body>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Assessment</th>
                  <th>Module</th>
                  <th>Type</th>
                  <th>Duration</th>
                  <th>Passing Score</th>
                  <th>Max Attempts</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {assessments.map((assessment) => (
                  <tr key={assessment.id}>
                    <td>
                      <strong>{assessment.title}</strong>
                      {assessment.description && (
                        <div><small className="text-muted">{assessment.description}</small></div>
                      )}
                    </td>
                    <td>{getModuleName(assessment.moduleId)}</td>
                    <td>{getTypeBadge(assessment.type)}</td>
                    <td>{assessment.duration} min</td>
                    <td>
                      <Badge bg={assessment.passingScore >= 80 ? 'success' : assessment.passingScore >= 60 ? 'warning' : 'danger'}>
                        {assessment.passingScore}%
                      </Badge>
                    </td>
                    <td>
                      <Badge bg="info">{assessment.maxAttempts}</Badge>
                    </td>
                    <td>{getStatusBadge(assessment.status)}</td>
                    <td>
                      <Button 
                        variant="outline-primary" 
                        size="sm" 
                        className="me-1"
                        onClick={() => handleShowModal(assessment)}
                      >
                        Edit
                      </Button>
                      <Button 
                        variant="outline-success" 
                        size="sm"
                        className="me-1"
                      >
                        Questions
                      </Button>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => handleDelete(assessment.id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
                {assessments.length === 0 && (
                  <tr>
                    <td colSpan="8" className="text-center text-muted py-4">
                      No assessments found. Create your first assessment to get started.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        {/* Add/Edit Assessment Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {editingAssessment ? 'Edit Assessment' : 'Create New Assessment'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Assessment Title *</Form.Label>
                    <Form.Control
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., Module 1 Quiz"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status *</Form.Label>
                    <Form.Select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="draft">Draft</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="archived">Archived</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Assessment instructions and description"
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Module</Form.Label>
                    <Form.Select
                      name="moduleId"
                      value={formData.moduleId}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Module</option>
                      {modules.map(module => (
                        <option key={module.id} value={module.id}>
                          {module.title}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Assessment Type *</Form.Label>
                    <Form.Select
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="quiz">Quiz</option>
                      <option value="assignment">Assignment</option>
                      <option value="exam">Exam</option>
                      <option value="project">Project</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Duration (minutes)</Form.Label>
                    <Form.Control
                      type="number"
                      name="duration"
                      value={formData.duration}
                      onChange={handleInputChange}
                      min="1"
                      max="480"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Passing Score (%)</Form.Label>
                    <Form.Control
                      type="number"
                      name="passingScore"
                      value={formData.passingScore}
                      onChange={handleInputChange}
                      min="0"
                      max="100"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Max Attempts</Form.Label>
                    <Form.Control
                      type="number"
                      name="maxAttempts"
                      value={formData.maxAttempts}
                      onChange={handleInputChange}
                      min="1"
                      max="10"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                {editingAssessment ? 'Update Assessment' : 'Create Assessment'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </>
  );
};

export default Assessments;
